// Aba "Pacientes Unimed Brasil" da tela Envio ao Carefy: cada linha é um paciente (estilo dos cards do
// Projetos IW), com a barra do mês logo abaixo:
//   vermelha = passou o prazo e falta relatório; azul = enviando, dentro do prazo;
//   verde = tudo enviado; branca = nada enviado ainda no mês (dentro do prazo).
// Clicando, abre o que o paciente precisa enviar no mês (por profissional e janela) e o que já foi;
// dá para marcar à mão o que a equipe já lançou direto no Carefy.
import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, Upload } from 'lucide-react';
import { supabase } from '../supabase';

// profissões que já têm leitor do PDF do IW (as outras: só marcar à mão)
const COM_LEITOR = new Set(['enfermagem', 'medico', 'fisioterapia', 'fonoaudiologia', 'terapia_ocupacional', 'nutricao', 'psicologia']);

const P = { bg: '#f8fafc', white: '#fff', border: '#e2e8f0', text: '#1e293b', muted: '#64748b', primary: '#4f46e5' };
const SIT = {
  atrasado: { nome: 'Atrasado', cor: '#dc2626', fundo: '#fee2e2' },
  andamento: { nome: 'Enviando (no prazo)', cor: '#2563eb', fundo: '#dbeafe' },
  'nao-iniciado': { nome: 'Nada enviado ainda', cor: '#94a3b8', fundo: '#f1f5f9' },
  concluido: { nome: 'Tudo enviado', cor: '#16a34a', fundo: '#dcfce7' },
  'sem-pad': { nome: 'Sem PAD', cor: '#a855f7', fundo: '#f3e8ff' },
};
const ORDEM = ['atrasado', 'andamento', 'nao-iniciado', 'sem-pad', 'concluido'];
const fmt = iso => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');
const fmtCompleto = iso => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '—');
const FREQS = ['Quinzenal', 'Mensal', '1x semana', '2x semana', '3x semana', '4x semana', '5x semana'];

async function chamar(corpo) {
  const { data: { session } } = await supabase.auth.getSession();
  const r = await fetch('/api/carefy', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` },
    body: JSON.stringify(corpo),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `Erro ${r.status}`);
  return j;
}

function Barra({ l }) {
  const s = SIT[l.situacao] || SIT['nao-iniciado'];
  const pct = l.total ? Math.round((l.enviados / l.total) * 100) : 0;
  const fill = l.situacao === 'atrasado' ? '#dc2626' : l.situacao === 'concluido' ? '#16a34a' : '#2563eb';
  return (
    <div title={`${s.nome} · ${l.enviados} de ${l.total}`} style={{ height: 7, borderRadius: 999, background: l.situacao === 'atrasado' ? '#fecaca' : '#fff', border: `1px solid ${l.situacao === 'nao-iniciado' ? '#cbd5e1' : s.cor}`, overflow: 'hidden' }}>
      <div style={{ width: `${l.situacao === 'atrasado' && !pct ? 100 : pct}%`, height: '100%', background: l.situacao === 'atrasado' && !pct ? '#fca5a5' : fill, transition: 'width .3s' }} />
    </div>
  );
}

export default function PendenciasCarefy({ onEnviar, onValidar }) {
  const [comp, setComp] = useState(() => new Date(Date.now() - 3 * 3600e3).toISOString().slice(0, 7));
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [aberto, setAberto] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [busca, setBusca] = useState('');
  const [unidade, setUnidade] = useState('');
  const [salvando, setSalvando] = useState('');
  const [novoProf, setNovoProf] = useState({ profissao: '', frequencia: 'Quinzenal' });
  const [subindo, setSubindo] = useState('');
  const entradaTabela = React.useRef(null);
  // tabela de evoluções do IW (TDWD_*.csv): até o IW mandar todo dia pelo FTP, sobe aqui
  const subirTabela = async arquivos => {
    setErro('');
    for (const a of [...(arquivos || [])]) {
      setSubindo(`Lendo ${a.name}…`);
      try {
        const csv = new TextDecoder('latin1').decode(await a.arrayBuffer());
        const j = await chamar({ acao: 'importarEvolucoes', csv, arquivo: a.name });
        setSubindo(`${a.name}: ${j.evolucoes} evoluções (${j.paraCarefy} de enfermagem/médico para o Carefy)`);
      } catch (e) { setErro(`${a.name}: ${e.message}`); setSubindo(''); return; }
    }
    await carregar();
  };

  const carregar = async () => {
    setCarregando(true); setErro('');
    try { setDados(await chamar({ acao: 'pendencias', competencia: comp })); } catch (e) { setErro(e.message); }
    setCarregando(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [comp]);

  const marcar = async (ids, enviado) => {
    setSalvando(ids.join(',')); setErro('');
    try { await chamar({ acao: 'marcar', ids, enviado }); await carregar(); } catch (e) { setErro(e.message); }
    setSalvando('');
  };
  const ajustarPad = async (atendimento, profissao, frequencia, remover) => {
    setSalvando(`pad-${atendimento}`); setErro('');
    try { await chamar({ acao: 'pad', atendimento, profissao, frequencia, remover }); await carregar(); setNovoProf({ profissao: '', frequencia: 'Quinzenal' }); } catch (e) { setErro(e.message); }
    setSalvando('');
  };

  const q = busca.trim().toLowerCase();
  const linhas = useMemo(() => (dados?.linhas || [])
    .filter(l => (!filtro || l.situacao === filtro) && (!unidade || l.unidade === unidade) && (!q || l.nome.toLowerCase().includes(q)))
    .sort((a, b) => ORDEM.indexOf(a.situacao) - ORDEM.indexOf(b.situacao) || (a.proximoPrazo || 'z').localeCompare(b.proximoPrazo || 'z') || a.nome.localeCompare(b.nome)), [dados, filtro, unidade, q]);

  const chip = (fundo, cor) => ({ fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: fundo, color: cor, whiteSpace: 'nowrap', display: 'inline-block' });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <label style={{ fontSize: '0.8rem', color: P.muted, display: 'flex', gap: 6, alignItems: 'center' }}>
          Mês <input type="month" value={comp} onChange={e => e.target.value && setComp(e.target.value)} style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: '5px 8px' }} />
        </label>
        {dados && ORDEM.map(k => (
          <button key={k} type="button" onClick={() => setFiltro(filtro === k ? '' : k)}
            style={{ ...chip(filtro === k ? SIT[k].cor : SIT[k].fundo, filtro === k ? '#fff' : SIT[k].cor), border: `1px solid ${SIT[k].cor}`, cursor: 'pointer', padding: '5px 12px', fontSize: '0.78rem' }}>
            {SIT[k].nome} · {dados.resumo[k] || 0}
          </button>
        ))}
        <select value={unidade} onChange={e => setUnidade(e.target.value)} style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: '6px 8px', fontSize: '0.8rem' }}>
          <option value="">Vitalcare e HomeBaby</option><option value="Vitalcare">Vitalcare</option><option value="HomeBaby">HomeBaby</option>
        </select>
        <label style={{ position: 'relative', marginLeft: 'auto' }}>
          <span style={{ position: 'absolute', left: -9999 }}>Buscar paciente</span>
          <Search size={14} style={{ position: 'absolute', left: 9, top: 9, color: '#94a3b8' }} />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Paciente" style={{ padding: '6px 8px 6px 28px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.8rem', width: 200 }} />
        </label>
        <button type="button" onClick={() => entradaTabela.current?.click()} title="Subir a tabela de evoluções exportada do IW (TDWD_….csv)"
          style={{ border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, display: 'inline-flex', gap: 5, alignItems: 'center', color: P.text }}>
          <Upload size={14} /> Tabela do IW
        </button>
        <input ref={entradaTabela} type="file" accept=".csv,text/csv" multiple style={{ display: 'none' }} onChange={e => { subirTabela(e.target.files); e.target.value = ''; }} />
        <button type="button" onClick={carregar} title="Atualizar" style={{ border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: '6px 9px', cursor: 'pointer' }}><RefreshCw size={14} /></button>
      </div>
      {dados && (
        <div style={{ fontSize: '0.78rem', color: P.muted }}>
          {dados.resumo.pacientes} pacientes Unimed Brasil em atendimento · {dados.resumo.enviados} de {dados.resumo.relatorios} relatórios do mês enviados ·
          ID e Pontual: até dia 15 e até o último dia · AD Terapias: até dia 20 · avaliação inicial: assim que registrar no IW
        </div>
      )}
      {subindo && <div style={{ background: '#eef2ff', color: '#3730a3', borderRadius: 10, padding: '8px 14px', fontSize: '0.8rem' }}>{subindo}</div>}
      {erro && <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 10, padding: '10px 14px', fontSize: '0.85rem' }}>{erro}</div>}
      {carregando && !dados && <div style={{ color: P.muted }}>Montando a lista (pacientes do IW, PAD e envios)…</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {linhas.map(l => {
          const s = SIT[l.situacao];
          const exp = aberto === l.atendimento;
          const pendentes = l.itens.filter(i => !i.enviado);
          return (
            <div key={l.atendimento} style={{ background: l.situacao === 'concluido' ? '#f0fdf4' : '#eef1f4', borderRadius: 10, borderLeft: `5px solid ${s.cor}`, padding: '10px 14px' }}>
              <div onClick={() => setAberto(exp ? null : l.atendimento)} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') setAberto(exp ? null : l.atendimento); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <span style={{ fontSize: 13, color: P.muted, transform: exp ? 'rotate(90deg)' : 'none', transition: 'transform .2s' }}>▶</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, color: P.text, fontSize: '0.95rem' }}>{l.nome}</div>
                  <div style={{ fontSize: '0.74rem', color: P.muted }}>
                    {l.unidade} · {[l.programa, l.detalhe].filter(Boolean).join(' — ') || 'tipo de PAD não informado no IW'} · implantação {fmtCompleto(l.implantacao)} · IW {l.atendimento}
                  </div>
                </div>
                <div style={{ textAlign: 'right', fontSize: '0.75rem', color: P.muted, whiteSpace: 'nowrap' }}>
                  <div style={{ fontWeight: 800, color: s.cor, fontSize: '0.85rem' }}>{l.total ? `${l.enviados} de ${l.total}` : 'sem PAD'}</div>
                  {l.proximoPrazo && <div>próximo prazo {fmt(l.proximoPrazo)}</div>}
                </div>
              </div>
              <div style={{ marginTop: 8 }}><Barra l={l} /></div>

              {exp && (
                <div style={{ marginTop: 12, background: P.white, borderRadius: 10, border: `1px solid ${P.border}`, padding: 12 }}>
                  {!l.tipoJanela && <div style={{ fontSize: '0.8rem', color: '#92400e', marginBottom: 8 }}>O IW não diz se o PAD é ID, AD ou Pontual — considerado mensal (até dia 20).</div>}
                  {l.itens.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', minWidth: 640 }}>
                        <thead><tr>{['Profissional', 'Janela', 'Prazo', 'Tipo', 'Situação', ''].map(h => <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: P.muted, fontSize: '0.68rem', textTransform: 'uppercase', borderBottom: `1px solid ${P.border}` }}>{h}</th>)}</tr></thead>
                        <tbody>
                          {l.itens.map(i => (
                            <tr key={i.id} style={{ borderTop: '1px solid #f1f5f9', background: i.atrasado ? '#fff7f7' : undefined }}>
                              <td style={{ padding: '6px 8px', fontWeight: 700 }}>{i.profissaoNome}<div style={{ fontSize: '0.7rem', color: P.muted, fontWeight: 500 }}>PAD: {i.frequencia || '—'}{i.origemPad === 'manual' ? ' (incluído à mão)' : ''}</div></td>
                              <td style={{ padding: '6px 8px' }}>{i.janelaNome}</td>
                              <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmtCompleto(i.prazo)}</td>
                              <td style={{ padding: '6px 8px' }}>{i.tipo}</td>
                              <td style={{ padding: '6px 8px' }}>
                                {i.enviado
                                  ? <span style={chip('#dcfce7', '#166534')} title={`${i.envio?.origem === 'quality' ? 'Enviado pelo Quality' : 'Marcado à mão'} em ${fmtCompleto(i.envio?.em)} por ${i.envio?.por || '—'}`}>✓ {i.envio?.origem === 'quality' ? 'enviado pelo Quality' : 'enviado (marcado)'}</span>
                                  : i.atrasado ? <span style={chip('#fee2e2', '#991b1b')}>atrasado</span> : <span style={chip('#dbeafe', '#1e40af')}>pendente</span>}
                                {!i.enviado && i.evolucao && (
                                  <div style={{ fontSize: '0.7rem', color: '#166534', marginTop: 3 }} title={i.evolucao.template}>📄 evolução no IW {fmt(i.evolucao.data)}{i.evolucao.hora ? ` ${i.evolucao.hora}` : ''} · {i.evolucao.autor}</div>
                                )}
                              </td>
                              <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                <label style={{ fontSize: '0.75rem', display: 'inline-flex', gap: 4, alignItems: 'center', cursor: 'pointer', marginRight: 8 }} title="Marque se já foi lançado no Carefy">
                                  <input type="checkbox" checked={i.enviado} disabled={!!salvando || i.envio?.origem === 'quality'} onChange={e => marcar([i.id], e.target.checked)} /> já enviado
                                </label>
                                {!i.enviado && i.evolucao && onValidar && (
                                  <button type="button" onClick={() => onValidar(i.profissao, i.evolucao.id)} title="Abre o formulário do Carefy já preenchido com a evolução da tabela do IW, para conferir antes de enviar"
                                    style={{ border: 0, background: '#16a34a', color: '#fff', borderRadius: 6, padding: '4px 10px', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer', marginRight: 6 }}>Validar</button>
                                )}
                                {!i.enviado && COM_LEITOR.has(i.profissao) && onEnviar && (
                                  <button type="button" onClick={() => onEnviar(i.profissao)} style={{ border: 0, background: P.primary, color: '#fff', borderRadius: 6, padding: '4px 10px', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer' }} title="Arrastar o PDF da evolução do IW">PDF</button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 10 }}>
                    {pendentes.length > 0 && (
                      <button type="button" disabled={!!salvando} onClick={() => { if (window.confirm(`Marcar os ${pendentes.length} relatório(s) pendentes de ${l.nome} como já enviados no Carefy?`)) marcar(pendentes.map(i => i.id), true); }}
                        style={{ border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: '6px 12px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>✓ Marcar todos como enviados</button>
                    )}
                    <span style={{ fontSize: '0.75rem', color: P.muted, marginLeft: 'auto' }}>PAD:</span>
                    {[...new Map(l.itens.map(i => [i.profissao, i])).values()].map(i => (
                      <span key={i.profissao} style={{ ...chip('#f1f5f9', '#334155'), display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                        {i.profissaoNome} · {i.frequencia || '—'}
                        <button type="button" title="Tirar do PAD" disabled={!!salvando} onClick={() => { if (window.confirm(`Tirar ${i.profissaoNome} do PAD de ${l.nome}?`)) ajustarPad(l.atendimento, i.profissao, '', true); }}
                          style={{ border: 0, background: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0 }}>✕</button>
                      </span>
                    ))}
                    <select value={novoProf.profissao} onChange={e => setNovoProf(n => ({ ...n, profissao: e.target.value }))} style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: '4px 6px', fontSize: '0.75rem' }}>
                      <option value="">+ profissional no PAD</option>
                      {(dados?.profissoes || []).filter(p => !l.itens.some(i => i.profissao === p.k)).map(p => <option key={p.k} value={p.k}>{p.nome}</option>)}
                    </select>
                    {novoProf.profissao && (
                      <>
                        <select value={novoProf.frequencia} onChange={e => setNovoProf(n => ({ ...n, frequencia: e.target.value }))} style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: '4px 6px', fontSize: '0.75rem' }}>
                          {FREQS.map(f => <option key={f}>{f}</option>)}
                        </select>
                        <button type="button" disabled={!!salvando} onClick={() => ajustarPad(l.atendimento, novoProf.profissao, novoProf.frequencia, false)}
                          style={{ border: 0, background: P.primary, color: '#fff', borderRadius: 6, padding: '4px 10px', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer' }}>Incluir</button>
                      </>
                    )}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 6 }}>O PAD vem das visitas do IW dos últimos 120 dias; profissional com todas as visitas feitas pode não aparecer — inclua à mão.</div>
                </div>
              )}
            </div>
          );
        })}
        {dados && !linhas.length && <div style={{ color: P.muted, padding: 20, textAlign: 'center' }}>Nenhum paciente neste filtro.</div>}
      </div>
    </div>
  );
}
